import React, { MouseEvent, useState, useEffect } from 'react';
import styled from 'styled-components';
import WishlistIconButton from '@westwing/ui-kit/WishlistIconButton';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import useProductsWishlist from 'Client/components/ProductDetailPage/hooks/useProductsWishlist';
import { ProductInterface, WishListTrackingAction } from 'Client/redux/products/types';
import { PageType, useCurrentPageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { ProductListingPages } from 'Client/hooks/UrlManager/types';
import { useLaunchDarklyTracker } from 'Client/LaunchDarklyTracker';
import useConfig from 'Client/hooks/useConfig';
import useRouter from 'Client/hooks/useRouter';

interface Props {
    product: ProductInterface;
}

const StyledWishlistIconButton = styled(WishlistIconButton)`
    margin-bottom: auto;
`;

export const ConnectedWishlistBadge = ({ product }: Props) => {
    const { isInProductsWishlist, addToProductsWishlist, removeFromProductsWishlist } = useProductsWishlist();
    const { wishListTrackingFn, addToWishListExperimentFn } = product;
    const [isSelected, setSelected] = useState(isInProductsWishlist(product.simpleSku));
    const launchDarklyTracker = useLaunchDarklyTracker();
    const { pageType } = useCurrentPageType();
    const isListingPage = ProductListingPages.includes(pageType);
    const { currency } = useConfig();
    const {
        host,
        location: { pathname },
    } = useRouter();

    const absoluteUrl = `${host}${pathname}`;

    useEffect(() => {
        setSelected(isInProductsWishlist(product.simpleSku));
    }, [isInProductsWishlist, product]);

    // eslint-disable-next-line complexity
    const handleStateChange = (e: MouseEvent<HTMLElement>, selected: boolean): void => {
        e.preventDefault();
        const isAllowed = selected
            ? addToProductsWishlist(product.simpleSku)
            : removeFromProductsWishlist(product.simpleSku);
        setSelected(!isAllowed ? false : selected);
        const trackingType = !isSelected ? WishListTrackingAction.ADD : WishListTrackingAction.REMOVE;
        if (wishListTrackingFn) {
            wishListTrackingFn({
                action: trackingType,
                sku: product.simpleSku,
                name: product.name,
                brand: product.brand,
                price: product.price,
                currency,
                url: absoluteUrl,
            });
        }

        const shouldTrackABT50 = isListingPage && ![PageType.NEWPRODUCTS, PageType.ALLPRODUCTS].includes(pageType);
        if (isDesktop() && launchDarklyTracker && !isSelected && shouldTrackABT50) {
            launchDarklyTracker.trackMetric('bella-add-to-wishlist-plp-ABT50');
        }

        if (!isSelected && addToWishListExperimentFn) {
            addToWishListExperimentFn();
        }
    };

    return <StyledWishlistIconButton onClick={e => handleStateChange(e, !isSelected)} isSelected={isSelected} />;
};
